// lib
import * as R from "ramda";
// src
import i18n from "i18n/proxy";
import { FAV_CATEGORY_PREFIX, PresetType } from "config/constants";
import { detach } from "utils/presets";

const NEW_CATEGORY_NAME = i18n.t("favorites.newCategory");

const createCategory = (items) => {
  const maxId = R.pipe(
    R.filter(({ type }) => type === PresetType.CATEGORY),
    R.map(({ id }) => parseInt(id.split(":")[1], 10)),
    R.filter((counter) => !Number.isNaN(counter)),
    (counters) => R.apply(Math.max, [0, ...counters]),
  )(items);
  const countNewNames = R.length(R.filter(({ name }) => name.startsWith(NEW_CATEGORY_NAME), items));
  const id = `${FAV_CATEGORY_PREFIX}:${maxId + 1}`;
  const newName = countNewNames ? `${NEW_CATEGORY_NAME} #${countNewNames + 1}` : NEW_CATEGORY_NAME;
  return { id, type: PresetType.CATEGORY, name: newName, index: items.length, editing: true };
};

export const initState = (favorites) => {
  const items = favorites.map((i) => ({ ...i, editing: false }));
  return {
    items,
    prevFavorites: favorites,
    modified: false,
    removedCategoryIds: [],
    resetById: {},
  };
};

export const serializeToPrefs = ({ items, removedCategoryIds, resetById }) => {
  const cleanedItems = R.map(R.omit(["editing", "index"]), items);
  const byId = R.reduce(
    (acc, preset) => {
      return R.assoc(preset.id, preset, acc);
    },
    {},
    cleanedItems,
  );
  const favoriteIds = R.map(({ id }) => id, cleanedItems);
  return { byId: R.mergeLeft(resetById, byId), favoriteIds, removedCategoryIds };
};

export const reducer = (state, action) => {
  switch (action.type) {
    case "update": {
      return {
        ...state,
        items: R.adjust(action.index, R.mergeLeft(action.payload), state.items),
        modified: true,
      };
    }
    case "remove": {
      const item = state.items[action.index];
      const { id: removedId, type } = item;
      const isCategory = type === PresetType.CATEGORY;

      const children = isCategory
        ? R.filter(({ underId }) => underId === removedId, state.items)
        : [];

      const toRemove = isCategory ? [item, ...children] : [item];
      const toRemoveIds = R.pluck("id", toRemove);
      const notCategoryToRemove = isCategory ? children : [item];

      const getNewResetById = () => {
        // restore refName and remove underId
        const detachedItems = R.map(detach, notCategoryToRemove);
        return R.indexBy(R.prop("id"), detachedItems);
      };

      const newItems = R.reject(({ id }) => toRemoveIds.includes(id), state.items);

      // resetById used to override main preset reducer byId
      // removedCategoryIds used to clean main preset reducer byId
      return {
        ...state,
        items: newItems.map((i, index) => ({
          ...i,
          index,
        })),
        resetById: R.mergeLeft(getNewResetById(), state.resetById),
        removedCategoryIds: isCategory
          ? R.append(removedId, state.removedCategoryIds)
          : state.removedCategoryIds,
        modified: true,
      };
    }
    case "edit": {
      // disable all and enable one
      return {
        ...state,
        items: R.adjust(
          action.index,
          R.mergeLeft({ editing: true }),
          R.map(R.assoc("editing", false), state.items),
        ),
      };
    }
    case "rename": {
      return {
        ...state,
        items: R.adjust(
          action.index,
          (prev) => ({
            ...prev,
            refName: prev.refName || prev.name, // don't override refName
            name: action.payload,
            editing: false,
          }),
          state.items,
        ),
        modified: true,
      };
    }
    case "move": {
      if (action.from === action.to) return state; // not modified

      let { items } = state;
      const isCategory = items[action.from].type === PresetType.CATEGORY;
      if (!isCategory) {
        const isForward = action.to > action.from;
        const under = R.findLast(({ type, index }) => {
          if (type !== PresetType.CATEGORY) return false;
          return isForward ? action.to >= index : action.to > index;
        }, state.items);
        items = R.adjust(
          action.from,
          under ? R.mergeLeft({ underId: under.id }) : R.dissoc("underId"),
          state.items,
        );
      }

      const movedItems = R.move(action.from, action.to, items).map((i, index) => ({
        ...i,
        index,
      }));
      return {
        ...state,
        items: movedItems,
        modified: true,
      };
    }
    case "addCategory": {
      const itemsDisabled = R.map(R.assoc("editing", false), state.items);
      return {
        ...state,
        items: R.append(createCategory(state.items), itemsDisabled),
        modified: true,
      };
    }
    case "unedit": {
      return {
        ...state,
        items: R.adjust(action.index, R.mergeLeft({ editing: false }), state.items),
        modified: true,
      };
    }
    case "unedit-all": {
      return {
        ...state,
        items: R.map(R.assoc("editing", false), state.items),
      };
    }
    case "init": {
      return initState(action.payload);
    }
    case "reinit": {
      return initState(state.prevFavorites);
    }
    default:
      return state;
  }
};

// transform flat items array (from state) to grouped items
export const createTree = (items) => {
  const parents = R.filter(({ underId }) => !underId, items);
  const children = R.reject(({ underId }) => !underId, items);

  return parents
    .reduce(
      (acc, parent, index) => {
        for (const child of children) {
          if (child.underId === parent.id) acc[index].push(child);
        }
        return acc;
      },
      R.map((p) => [p], parents),
    )
    .map((group) => {
      group[0].childrenCount = group.length - 1;
      return group;
    });
};
